import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {MapMarkerOutlineIcon, PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {IPoiSet, NearestPoiTypeStats} from "../../offer/detail/actions/fetch_offer_detail_poi";
import {Country} from "../../region/types/Country";
import {IPoi} from "../types/IPoi";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";
import {getHasCountryPois} from "../utils/get_has_country_pois";
import {getPoiPluralizeLabel, PoiType} from "../utils/PoiType";
import {PoiItemWithLabel} from "./PoiItemWithLabel";
import {RegionCenterDriveTime} from "./RegionCenterDriveTime";
import {RegionTransportRoutes} from "./RegionTransportRoutes";

interface IProps {
    offer: {
        geo_point: {coordinates: [number, number]};
        stats: {
            distance_from_region: {
                center: boolean;
                coords: [number, number];
                region: string;
            };
        } | null;
    };
    country: Country;
    busPoi: IPoi | IPoiSet | null;
    tramPoi: IPoi | IPoiSet | null;
    subwayPoi: IPoi | IPoiSet | null;
    railwayPoi: IPoi | IPoiSet | null;
    nearestPoisCount: Record<PoiType, NearestPoiTypeStats> | undefined;
    routes: {
        busRoutes: IPublicTransportRoute[] | null;
        railwayRoutes: IPublicTransportRoute[] | null;
        subwayRoutes: IPublicTransportRoute[] | null;
        tramRoutes: IPublicTransportRoute[] | null;
    } | null;
    activePoiRoute: IPublicTransportRoute | null;
    onTransportRouteClick: (route: IPublicTransportRoute) => void;
    nearbySubject: string;
    className?: string;
}

export function LocationPoisSummary(props: IProps) {
    const {offer, country, busPoi, tramPoi, subwayPoi, railwayPoi, nearestPoisCount, routes, activePoiRoute, onTransportRouteClick, nearbySubject} = props;

    const hasTransportPois = Boolean(subwayPoi || railwayPoi || tramPoi || busPoi);
    const hasRoutes = Boolean(routes && (routes.busRoutes?.length || routes.railwayRoutes?.length || routes.subwayRoutes?.length || routes.tramRoutes?.length));

    const poiCountLabels = useMemo(() => {
        if (!nearestPoisCount) {
            return [] as string[];
        }

        return (Object.keys(nearestPoisCount) as PoiType[])
            .filter((key) => ![PoiType.TRANSPORT, PoiType.ALL].includes(key))
            .reduce<string[]>((acc, key) => {
                const count = nearestPoisCount[key]?.count ?? 0;

                if (count) {
                    acc.push(getPoiPluralizeLabel(key, count));
                }

                return acc;
            }, []);
    }, [nearestPoisCount]);

    const hasOtherPois = poiCountLabels.length > 0;

    if (!getHasCountryPois(country) || (!hasTransportPois && !hasOtherPois)) {
        return null;
    }

    return (
        <div className={cx(summaryStyle, props.className)}>
            {hasTransportPois && (
                <div className={columnStyle}>
                    {subwayPoi && (
                        <PoiItemWithLabel
                            icon={<PoiSubwayIcon size="2.4" wrapperSize="2" wrapperColor="transparent" />}
                            label="Metro - "
                            poi={subwayPoi}
                            showName
                        />
                    )}
                    {!subwayPoi && railwayPoi && (
                        <PoiItemWithLabel
                            icon={<PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                            label="Stacja kolejowa - "
                            poi={railwayPoi}
                            showName
                        />
                    )}
                    {tramPoi && (
                        <PoiItemWithLabel
                            icon={<PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                            label="Przystanek Tramwajowy"
                            poi={tramPoi}
                        />
                    )}
                    {busPoi && (
                        <PoiItemWithLabel icon={<PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />} label="Przystanek Autobusowy" poi={busPoi} />
                    )}

                    <div className={indentStyle}>
                        <RegionCenterDriveTime offer={offer} />
                    </div>

                    {hasRoutes && routes && (
                        <div className={routesStyle}>
                            <Text as="div" variant="info_txt_1" color="var(--colors-secondary)">
                                Linie komunikacyjne w okolicy:
                            </Text>
                            <RegionTransportRoutes {...routes} activePoiRoute={activePoiRoute} onTransportRouteClick={onTransportRouteClick} />
                        </div>
                    )}
                </div>
            )}

            {hasOtherPois && (
                <div className={nearbyColumnStyle}>
                    <MapMarkerOutlineIcon size="1.6" wrapperSize="2" wrapperColor="var(--colors-primary)" />
                    <div className={nearbyTextStyle}>
                        <Text variant="body_copy_1">
                            <strong>W pobliżu 1 km</strong> od tej {nearbySubject} znajdują się:
                        </Text>
                        <Text variant="body_copy_1">{poiCountLabels.join(", ")}</Text>
                    </div>
                </div>
            )}
        </div>
    );
}

const summaryStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
    md: {
        flexDirection: "row",
        gap: 4
    }
});

const columnStyle = css({
    flex: 1,
    minWidth: 0
});

const indentStyle = css({
    pl: 3.5
});

const routesStyle = css({
    pl: 3.5,
    pt: 1.5
});

const nearbyColumnStyle = css({
    display: "flex",
    flex: 1,
    minWidth: 0,
    gap: 1,
    alignItems: "flex-start"
});

const nearbyTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});
import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {MapMarkerOutlineIcon, PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {IPoiSet, NearestPoiTypeStats} from "../../offer/detail/actions/fetch_offer_detail_poi";
import {Country} from "../../region/types/Country";
import {IPoi} from "../types/IPoi";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";
import {getHasCountryPois} from "../utils/get_has_country_pois";
import {getPoiPluralizeLabel, PoiType} from "../utils/PoiType";
import {PoiItemWithLabel} from "./PoiItemWithLabel";
import {RegionCenterDriveTime} from "./RegionCenterDriveTime";
import {RegionTransportRoutes} from "./RegionTransportRoutes";

interface IProps {
    offer: {
        geo_point: {coordinates: [number, number]};
        stats: {
            distance_from_region: {
                center: boolean;
                coords: [number, number];
                region: string;
            };
        } | null;
    };
    country: Country;
    busPoi: IPoi | IPoiSet | null;
    tramPoi: IPoi | IPoiSet | null;
    subwayPoi: IPoi | IPoiSet | null;
    railwayPoi: IPoi | IPoiSet | null;
    nearestPoisCount: Record<PoiType, NearestPoiTypeStats> | undefined;
    routes: {
        busRoutes: IPublicTransportRoute[] | null;
        railwayRoutes: IPublicTransportRoute[] | null;
        subwayRoutes: IPublicTransportRoute[] | null;
        tramRoutes: IPublicTransportRoute[] | null;
    } | null;
    activePoiRoute: IPublicTransportRoute | null;
    onTransportRouteClick: (route: IPublicTransportRoute) => void;
    nearbySubject: string;
    className?: string;
}

export function LocationPoisSummary(props: IProps) {
    const {offer, country, busPoi, tramPoi, subwayPoi, railwayPoi, nearestPoisCount, routes, activePoiRoute, onTransportRouteClick, nearbySubject} = props;

    const hasTransportPois = Boolean(subwayPoi || railwayPoi || tramPoi || busPoi);
    const hasRoutes = Boolean(routes && (routes.busRoutes?.length || routes.railwayRoutes?.length || routes.subwayRoutes?.length || routes.tramRoutes?.length));

    const poiCountLabels = useMemo(() => {
        if (!nearestPoisCount) {
            return [] as string[];
        }

        return (Object.keys(nearestPoisCount) as PoiType[])
            .filter((key) => ![PoiType.TRANSPORT, PoiType.ALL].includes(key))
            .reduce<string[]>((acc, key) => {
                const count = nearestPoisCount[key]?.count ?? 0;

                if (count) {
                    acc.push(getPoiPluralizeLabel(key, count));
                }

                return acc;
            }, []);
    }, [nearestPoisCount]);

    const hasOtherPois = poiCountLabels.length > 0;

    if (!getHasCountryPois(country) || (!hasTransportPois && !hasOtherPois)) {
        return null;
    }

    return (
        <div className={cx(summaryStyle, props.className)}>
            {hasTransportPois && (
                <div className={columnStyle}>
                    {subwayPoi && (
                        <PoiItemWithLabel
                            icon={<PoiSubwayIcon size="2.4" wrapperSize="2" wrapperColor="transparent" />}
                            label="Metro - "
                            poi={subwayPoi}
                            showName
                        />
                    )}
                    {!subwayPoi && railwayPoi && (
                        <PoiItemWithLabel
                            icon={<PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                            label="Stacja kolejowa - "
                            poi={railwayPoi}
                            showName
                        />
                    )}
                    {tramPoi && (
                        <PoiItemWithLabel
                            icon={<PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                            label="Przystanek Tramwajowy"
                            poi={tramPoi}
                        />
                    )}
                    {busPoi && (
                        <PoiItemWithLabel icon={<PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />} label="Przystanek Autobusowy" poi={busPoi} />
                    )}

                    <div className={indentStyle}>
                        <RegionCenterDriveTime offer={offer} />
                    </div>

                    {hasRoutes && routes && (
                        <div className={routesStyle}>
                            <Text as="div" variant="info_txt_1" color="var(--colors-secondary)">
                                Linie komunikacyjne w okolicy:
                            </Text>
                            <RegionTransportRoutes {...routes} activePoiRoute={activePoiRoute} onTransportRouteClick={onTransportRouteClick} />
                        </div>
                    )}
                </div>
            )}

            {hasOtherPois && (
                <div className={nearbyColumnStyle}>
                    <MapMarkerOutlineIcon size="1.6" wrapperSize="2" wrapperColor="var(--colors-primary)" />
                    <div className={nearbyTextStyle}>
                        <Text variant="body_copy_1">
                            <strong>W pobliżu 1 km</strong> od tej {nearbySubject} znajdują się:
                        </Text>
                        <Text variant="body_copy_1">{poiCountLabels.join(", ")}</Text>
                    </div>
                </div>
            )}
        </div>
    );
}

const summaryStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
    md: {
        flexDirection: "row",
        gap: 4
    }
});

const columnStyle = css({
    flex: 1,
    minWidth: 0
});

const indentStyle = css({
    pl: 3.5
});

const routesStyle = css({
    pl: 3.5,
    pt: 1.5
});

const nearbyColumnStyle = css({
    display: "flex",
    flex: 1,
    minWidth: 0,
    gap: 1,
    alignItems: "flex-start"
});

const nearbyTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});
